import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import GlobalStyles from './styles/GlobalStyles';
import Storefront from './pages/Storefront';
import Home from './pages/Home';
import Login from './pages/Login';
import SignUp from './pages/SignUp';

import Profile from './pages/Profile';
import EnhancedPlanner from './pages/Planner';
import SettingsPage from './pages/Settings';
import MyDayPage from './pages/MyDay';
import AIAssistant from './pages/AIAssistant';
import AppLayout from './components/AppLayout';


// Protected route component that checks auth status
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [user, loading] = useAuthState(auth);
  
  if (loading) {
    return null; // No loading screen - instant redirect
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return <AppLayout>{children}</AppLayout>;
};

// Wrapper component to provide profile data to Planner
const PlannerWrapper: React.FC = () => {
  const [user] = useAuthState(auth);
  const [profileData, setProfileData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;
      
      try {
        // Load actual profile data from Firestore - using userProfiles collection
        const profileDoc = await getDoc(doc(db, 'userProfiles', user.uid));
        
        if (profileDoc.exists()) {
          const userProfileData = profileDoc.data();
          
          // Extract energy level from mess test data or default to 3
          let energyLevel = 3;
          if (userProfileData.messTestData?.chorePreferences?.choreStyle) {
            const choreStyle = userProfileData.messTestData.chorePreferences.choreStyle;
            if (choreStyle === 'light' || choreStyle === 'minimal') {
              energyLevel = 2;
            } else if (choreStyle === 'moderate' || choreStyle === 'balanced') {
              energyLevel = 3;
            } else if (choreStyle === 'intensive' || choreStyle === 'thorough') {
              energyLevel = 4;
            }
          }
          
          // Extract safe foods from multiple sources
          const safeFoods = [
            ...(userProfileData.foodPreferences || []),
            ...(userProfileData.tasteTestData?.foodPreferences?.mealTypes || []),
            ...(userProfileData.tasteTestData?.foodPreferences?.snackTypes || []),
            ...(userProfileData.tasteTestData?.cookingHabits?.preferredMealTypes || [])
          ].filter((food, index, array) => array.indexOf(food) === index); // Remove duplicates
          
          // Extract favorite meals from taste test data
          const favoriteMeals = [
            ...(userProfileData.tasteTestData?.foodPreferences?.cuisines || []),
            ...(userProfileData.tasteTestData?.foodPreferences?.mealTypes || []),
            ...(userProfileData.tasteTestData?.cookingHabits?.preferredMealTypes || [])
          ].filter((meal, index, array) => array.indexOf(meal) === index); // Remove duplicates
          
          // Map userProfiles data structure to the expected planner structure
          const mappedProfileData = {
            household: {
              members: userProfileData.householdMembers || [
                { name: user.displayName || 'You', age: 'Adult' }
              ],
              includeInPlanning: true
            },
            householdMemberColors: userProfileData.householdMemberColors || {},
            food: {
              safeFoods: safeFoods,
              favoriteMeals: favoriteMeals,
              sensoryIssues: userProfileData.tasteTestData?.dietaryRestrictions?.map((r: any) => r.name) || [],
              lunchboxIdeas: true // Default to true, could be made configurable
            },
            meals: {
              homeCooked: userProfileData.tasteTestData?.cookingHabits?.frequency || '3-5',
              takeawayNight: { 
                has: !!userProfileData.takeawayNight, 
                day: userProfileData.takeawayNight || 'Friday' 
              },
              favoriteTakeaway: userProfileData.tasteTestData?.foodPreferences?.cuisines || []
            },
            chores: {
              energyLevel: energyLevel,
              chorePreference: userProfileData.messTestData?.chorePreferences?.choreStyle || 'moderate',
              difficultDays: userProfileData.messTestData?.noChoresDays || [],
              assignChores: userProfileData.messTestData?.choreAssignments?.length > 0 || true
            },
            organisation: {
              tools: [],
              wantsReminders: true,
              otherTools: ''
            },
            planner: {
              autoGenerate: true,
              frequency: 'weekly' as const,
              viewType: 'calendar' as const,
              calendarView: 'week' as const
            },
            // Include all the original userProfile data for direct access
            ...userProfileData
          };
          
          setProfileData(mappedProfileData);
          console.log('Profile data loaded in PlannerWrapper:', mappedProfileData);
          console.log('Energy level calculated:', energyLevel);
          console.log('Safe foods extracted:', safeFoods);
          console.log('Favorite meals extracted:', favoriteMeals);
          console.log('Difficult days:', userProfileData.messTestData?.noChoresDays || []);
          
          // Profile data successfully mapped and ready for planner
        } else {
          // Provide a default profile structure if no profile exists
          const defaultProfile = {
            household: {
              members: [
                { name: user.displayName || 'You', age: 'Adult' }
              ],
              includeInPlanning: true
            },
            householdMemberColors: {},
            food: {
              safeFoods: [],
              favoriteMeals: [],
              sensoryIssues: [],
              lunchboxIdeas: true
            },
            meals: {
              homeCooked: '3-5',
              takeawayNight: { has: false, day: 'Friday' },
              favoriteTakeaway: []
            },
            chores: {
              energyLevel: 3,
              chorePreference: 'moderate',
              difficultDays: [],
              assignChores: true
            },
            organisation: {
              tools: [],
              wantsReminders: true,
              otherTools: ''
            },
            planner: {
              autoGenerate: true,
              frequency: 'weekly' as const,
              viewType: 'calendar' as const,
              calendarView: 'week' as const
            }
          };
          
          setProfileData(defaultProfile);
          console.log('Default profile data set in PlannerWrapper:', defaultProfile);
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
        setProfileData({});
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();

    // Listen for profile updates
    const handleProfileUpdate = () => {
      console.log('Profile updated, reloading profile data in PlannerWrapper...');
      loadProfileData();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [user]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return <EnhancedPlanner profileData={profileData} />;
};

const App: React.FC = () => {
  const [user, loading] = useAuthState(auth);

  if (loading) {
    return null;
  }

  return (
    <Router>
      <GlobalStyles />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          
          {/* Protected routes with AppLayout */}

          
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/planner"
            element={
              <ProtectedRoute>
                <PlannerWrapper />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/my-day"
            element={
              <ProtectedRoute>
                <MyDayPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai-assistant"
            element={
              <ProtectedRoute>
                <AIAssistant />
              </ProtectedRoute>
            }
          />
          
          {/* Root route - show Storefront if not logged in, redirect to app if logged in */}
          <Route
            path="/"
            element={
              user ? <Navigate to="/app" /> : <Storefront />
            }
          />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    </Router>
  );
};

export default App;
